import React from 'react';
import { useParams } from 'react-router-dom';
import * as Scroll from 'react-scroll';
import styled from 'styled-components';

// Components
import TrusteeEventAttendees from './TrusteeEventAttendees';
import TrusteeEventDocuments from './TrusteeEventDocuments';
import TrusteeEventSessionsList from './TrusteeEventSessionsList';
import ErrorHandler from '../../../_molecules/ErrorHandler';
import SectionBreadcrumb from '../../../_molecules/SectionBreadcrumb';
import SectionBreadcrumbs from '../../../_molecules/SectionBreadcrumbs';
import SectionHeader from '../../../_molecules/SectionHeader';
import useEvent from '../../../../hooks/useEvent';
import ArrowDiagonal from '@icons/ArrowDiagonal';
import {
    ButtonPrimaryLarge,
    DivWithShadowAlternate,
    H3,
    H4,
    LabelSmall,
    LinkSmall,
    SectionWrapperInner,
    SectionWrapperOuter,
} from '@styled';

// Helpers
import colors from '@general/colors';
import { getDateRangeTextLong } from '@general/dates';
import useDocumentTitle from '@hooks/useDocumentTitle';
import useViewer from '@hooks/useViewer';
import TrackPageView from '@tracking/TrackPageView';
import LoadingArea from '@molecules/LoadingArea';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useEventFromAPI from '@hooks/apiLegacy/useEventFromAPI';
import useRegistrantClassFromAPI from '@hooks/apiLegacy/useRegistrantClassFromAPI';
import useEventEE from '@hooks/useEventEE';
import useMemberEventSessions from '@hooks/useMemberEventSessions';
import eventFromEEMock from './eventFromEEMock';

const { scroller } = Scroll;

const SectionContainerStyled = styled.div`
    margin-bottom: 64px;
`;

const HeroImage = styled.img`
    height: 342px;
    max-width: 100%;
    object-fit: cover;
    width: 100%;
`;

const HotelLinkStyled = styled(ButtonPrimaryLarge)`
    text-align: center;
    width: 100%;

    &:hover,
    &:focus {
        color: white !important;
    }
`;

const TrusteeEventDetailPage = () => {
    const { viewer } = useViewer();
    const { eventSlug } = useParams();
    const eventFromAlgolia = useEvent(eventSlug);
    const { event: eventFromEE } = useEventEE(eventSlug);
    const { event: eventFromTS } = useEventFromAPI({ code: eventSlug });
    const { memberEventSessions } = useMemberEventSessions({
        memberId: viewer?.recordId,
        eventCode: eventFromTS?.code,
    });

    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { registrantClass } = useRegistrantClassFromAPI({
        memberId: viewer?.recordId,
        cartId: cart?.id,
        eventId: eventFromTS?.id,
    });

    useDocumentTitle(eventFromEE?.title);


    if (!viewer) {
        return <LoadingArea />;
    }

    // if (!viewer.isTrustee) {
    if (viewer.company?.recordId !== 5094 && ![95243, 128345, 320103].includes(viewer?.recordId)) {
        window.location.href = '/my-icsc';
        return;
    }

    if (!eventFromAlgolia || !eventFromEE || !eventFromTS) {
        return <LoadingArea />;
    }

    if (!eventFromAlgolia.trustee_event) {
        window.location.href = `/my-icsc/events/${eventSlug}`;
        return;
    }

    const { title, meeting_start_time: startDate, meeting_end_time: endDate, image } = eventFromAlgolia;

    return (
        <ErrorHandler>
            <TrackPageView>
                <SectionWrapperOuter>
                    <SectionWrapperInner className="fade-in-fast">
                        <SectionContainerStyled>
                            <SectionHeader title="My ICSC Trustee Center" />

                            <HeroImage className="mb-3" src={image || '/assets/images/pattern-white.svg'} />

                            <div className="mb-1.5rem">
                                <SectionBreadcrumbs>
                                    <SectionBreadcrumb route="/my-icsc/trustees">Trustee Center</SectionBreadcrumb>
                                    <SectionBreadcrumb isActive>{title}</SectionBreadcrumb>
                                </SectionBreadcrumbs>
                            </div>

                            <H3 className="mb-1.25rem">{title}</H3>

                            <div className="mb-3rem">
                                <LabelSmall>{getDateRangeTextLong(startDate, endDate)}</LabelSmall>
                            </div>

                            <div className="flex flex-col gap-4">
                                <H4>Links</H4>

                                <div className="flex flex-col gap-4 ml-0.75rem">
                                    <div className="flex">
                                        <LinkSmall
                                            className="font-bold"
                                            href="#event-attendees"
                                            onClick={e => {
                                                e.preventDefault();
                                                scroller.scrollTo('event-attendees', {
                                                    smooth: true,
                                                });
                                            }}
                                        >
                                            Connect with Attendees
                                        </LinkSmall>
                                    </div>

                                    <div className="flex">
                                        <LinkSmall
                                            className="font-bold"
                                            href="#related-documents"
                                            onClick={e => {
                                                e.preventDefault();
                                                scroller.scrollTo('related-documents', {
                                                    smooth: true,
                                                });
                                            }}
                                        >
                                            Related Documents
                                        </LinkSmall>
                                    </div>

                                    {eventFromAlgolia.hotel_link && (
                                        <div className="flex">
                                            <LinkSmall
                                                className="font-bold flex items-center gap-2"
                                                href={eventFromAlgolia.hotel_link}
                                                target="_blank"
                                            >
                                                Hotel Accommodations <ArrowDiagonal color={colors.azure} />
                                            </LinkSmall>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </SectionContainerStyled>

                        <SectionContainerStyled>
                            <H4 className="mb-1.25rem">Schedule of Activities</H4>
                            <DivWithShadowAlternate className="p-2rem">
                                <TrusteeEventSessionsList
                                    eventFromAlgolia={eventFromAlgolia}
                                    eventFromEE={eventFromEE}
                                    eventFromTS={eventFromTS}
                                    registeredSessions={memberEventSessions}
                                    registrantClass={registrantClass}
                                />
                            </DivWithShadowAlternate>
                        </SectionContainerStyled>

                        <SectionContainerStyled id="event-attendees">
                            <TrusteeEventAttendees event={eventFromAlgolia} />
                        </SectionContainerStyled>

                        <div id="related-documents">
                            <TrusteeEventDocuments documents={eventFromEE.documents} />
                        </div>
                    </SectionWrapperInner>
                </SectionWrapperOuter>
            </TrackPageView>
        </ErrorHandler>
    );
};

export default TrusteeEventDetailPage;
